/**
 * Implementation: up to 6 weeks from kickoff. Longer-lead items, such as custom packaging,
 * continue into the retainer. The database applies the same 42 days
 * (supabase/migrations/20261006000001_implementation.sql); keep the two in step.
 */
export const IMPLEMENTATION_WEEKS = 6;
export const IMPLEMENTATION_DAYS = IMPLEMENTATION_WEEKS * 7;

export type ActionStatus = "todo" | "doing" | "done" | "blocked";

export const ACTION_STATUS_LABEL: Record<ActionStatus, string> = {
  todo: "To do",
  doing: "In progress",
  done: "Done",
  blocked: "Blocked",
};

export interface ImplementationAction {
  id?: string;
  audit_id?: string;
  lever: 1 | 2 | 3 | 4 | 5 | null;
  title: string;
  owner: string;
  due: string | null;
  status: ActionStatus;
  note: string;
  updated_at?: string;
}

const DAY = 864e5;
const toMs = (iso: string) => Date.parse(`${iso.slice(0, 10)}T12:00:00Z`);
const toIso = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/** The default end date: 6 weeks after the start. */
export function defaultImplementationEnd(start: string): string {
  return toIso(toMs(start) + IMPLEMENTATION_DAYS * DAY);
}

export interface ImplementationProgress {
  total: number;
  done: number;
  blocked: number;
  /** Not done and due before today */
  overdue: number;
  /** Whole weeks into implementation, 1-based; null before it starts */
  week: number | null;
  daysLeft: number | null;
  /** Past the end date with actions still open */
  overrun: boolean;
}

export function implementationProgress(
  actions: ImplementationAction[],
  start: string | null,
  end: string | null,
  today: string,
): ImplementationProgress {
  const t = toMs(today);
  const done = actions.filter((a) => a.status === "done").length;
  const open = actions.length - done;
  const week = start && t >= toMs(start) ? Math.floor((t - toMs(start)) / (7 * DAY)) + 1 : null;
  const daysLeft = end ? Math.round((toMs(end) - t) / DAY) : null;
  return {
    total: actions.length,
    done,
    blocked: actions.filter((a) => a.status === "blocked").length,
    overdue: actions.filter((a) => a.status !== "done" && a.due !== null && toMs(a.due) < t).length,
    week,
    daysLeft,
    overrun: daysLeft !== null && daysLeft < 0 && open > 0,
  };
}

/** Sorted for display and for the report: by lever (general last), then due date, then title. */
export function sortActions<T extends ImplementationAction>(actions: T[]): T[] {
  return [...actions].sort(
    (a, b) =>
      (a.lever ?? 9) - (b.lever ?? 9) ||
      (a.due ?? "9999").localeCompare(b.due ?? "9999") ||
      a.title.localeCompare(b.title),
  );
}

/**
 * A starting set of actions drawn from the lever results, for the founders to edit.
 * Only levers with a finding get an action, so nothing is invented.
 */
export function suggestedActions(input: {
  start: string;
  lever1?: { overchargedCount: number; nearBandCount: number } | null;
  lever2?: { savingCount: number; topFormat: string | null } | null;
  manual?: { lever: 3 | 4 | 5; annual_saving_pence: number | null }[];
}): ImplementationAction[] {
  const due = (weeks: number) => toIso(toMs(input.start) + weeks * 7 * DAY);
  const out: ImplementationAction[] = [];
  const add = (lever: ImplementationAction["lever"], title: string, weeks: number) =>
    out.push({ lever, title, owner: "", due: due(weeks), status: "todo", note: "" });
  const l1 = input.lever1;
  if (l1 && l1.overchargedCount > 0) add(1, `Raise billing errors with the carrier (${l1.overchargedCount} overcharged parcels)`, 1);
  if (l1 && l1.nearBandCount > 0) add(1, `Change packing so ${l1.nearBandCount} near-band parcels drop a band`, 2);
  const l2 = input.lever2;
  if (l2 && l2.savingCount > 0) {
    add(2, `Agree new formats for ${l2.savingCount} right-sizable parcels${l2.topFormat ? `, mainly ${l2.topFormat}` : ""}`, 1);
    add(2, "Order and trial the new formats on real orders", 3);
  }
  for (const m of input.manual ?? []) {
    if (!m.annual_saving_pence) continue;
    if (m.lever === 3) add(3, "Switch to the agreed lighter or cheaper materials", 4);
    if (m.lever === 4) add(4, "Agree new prices and terms with packaging suppliers", 4);
    if (m.lever === 5) add(5, "Negotiate carrier rates and move services", 5);
  }
  add(null, "Check savings on the first invoices after the changes", IMPLEMENTATION_WEEKS);
  return out;
}
